<template>
    <div 
        v-click-outside="closeDropdown" 
        class="btn-group" 
        :class="[disabled ? 'dropdown--disabled' : '']" 
        v-bind="$attrs" 
        :data-dropdown-layer="dataLayerId"
    >
        <button 
            type="button" 
            :class="[btnClasses, `button--${size}`, `dropdown-btn-${size}`, textTransformType]" 
            class="btn button--outline__secondary dropdown-btn" 
            :disabled="outerLoading"
            @click="toggleDropdown"
        >          
            <slot v-if="isCustom" name="custom-header" /> 

            <template v-else>
                <span v-if="reactiveSelected">
                    <template v-if="typeof reactiveSelected === 'string'">
                        <div>
                            <span>{{ reactiveSelected }}</span>
                        </div>
                    </template>
    
                    <template v-if="typeof reactiveSelected === 'object'">
                        <template v-if="!multiple">
                            <div>
                                <span>{{ computeOptionItem(reactiveSelected) }}</span>
                            </div>
                        </template>
    
                        <template v-else>
                            <div class="dropdown--selection">
                                <span v-for="item in reactiveSelected" :key="getUniqueId(item)" class="dropdown--item--multiple">
                                    <span class="dropdown--item-desc">{{ computeOptionItem(item) }}</span>
                                    <span class="pointer inline-block" @click.stop="$emit('remove', item)">
                                        <IconSvg name="close" width="16px" fill="#ced4da" />
                                    </span>
                                </span>
                            </div>
                        </template>
                    </template> 
                </span>
    
                <template v-else>
                    <p>{{ placeholder }}</p>
                </template>
                
                <span class="pointer inline-block dropdown-btn-icon">
                    <div v-if="outerLoading">
                        <PageSpinner
                            size="sm" 
                            :is-page="false"
                        />
                    </div>
                    <IconSvg v-else :name="showDropdown ? 'chevron-up' : 'chevron-down'" width="16px" fill="#ced4da" />
                </span>
            </template>
        </button>

        <transition name="fade">
            <div 
                v-if="showDropdown" 
                class="dropdown--menu w-100 p--0"
                :class="[!shadow ? 'no-shadow' : '', showDropdown ? 'show' : '', menuClass]"
            >     
                <slot v-if="isCustom" name="custom-body" />     
                <template v-else>
                    <form v-if="showSearchInput" class="dropdown-form" :class=" { 'active': inputFocus }" @submit.prevent="">
                        <input
                            ref="searchRef" 
                            v-model="searchQuery" 
                            name="search"
                            type="text" 
                            :placeholder="placeholder || `Search ${listType}...`"
                        > 
                        <span class="pointer" @click="clearSearch">
                            <icon-svg fill="rgba(194, 200, 212, 1)" :name="searchQuery ? 'close' : 'search'" width="16px" /> 
                        </span>
                    </form>
        
                    <div>
                        <template v-if="loading">
                            <div class="text--color-title text--sm p--25 text-center">
                                <PageSpinner class="mt--5 mb--5" size="sm" />
                            </div>
                        </template>
            
                        <template v-else>
                            <ul 
                                v-if="options.length" 
                                class="dropdown-list mb--0" 
                                :class="[listClass, options.length < 6 ? 'h--auto' : '']"
                            >
                                <div v-if="showListTitle" class="dropdown--title">
                                    All {{ listType }}
                                </div>
                                
                                <li
                                    v-for="(item) in options"
                                    :key="getUniqueId(item)" 
                                    :class="[listItemClass]"
                                    tabindex="0" 
                                    class="dropdown-item dropdown--item"
                                    @click="onSelect(item)" 
                                    @keyup.enter="onSelect(item)"
                                >
                                    <div class="flex flex__item--grow align-items-center">
                                        <slot name="prefix" :item="item" />
            
                                        <p v-if="!customText" class="dropdown--item-label">
                                            {{ computeOptionItem(item) }}
                                        </p>
                                        <slot v-else name="text" :item="item" />
    
                                        <slot name="suffix" :item="item" />
                                    </div>
                                </li>
                            </ul>
            
                            <div v-else class="p--25 m-auto flex flex-col align-items-center w-100">
                                <IconSvg name="search" width="24px" fill="#ced4da" class="not-found-icon" />
                                <p class="text--color-title text--sm p--25 text-center">
                                    No results found
                                </p>
                            </div>
                        </template>
                    </div>
                </template>       
            </div>
        </transition>
    </div>
</template>
  
<script>  
import IconSvg from "../icons/Icon-Svg.vue";
import PageSpinner from "../loaders/PageSpinner.vue";
import { debounce } from "@/utils/helpers";

export default {
    name: "CustomDropdown",

    components: { IconSvg, PageSpinner },

    directives: {
        // "click-outside": {
        //     beforeMount(el, binding) { // Renamed from 'bind'
        //         //     // Check if the click was outside the el and its children
        //         //     // if (!(el === event.target || el.contains(event.target))) {
        //         //     //     // Call the method provided in the directive's value
        //         //     //     binding.value(event);
        //         //     // }
        //         // };
        //         document.addEventListener("click", el.clickOutsideEvent);
        //     },
        //     unmounted(el) { // Renamed from 'unbind'
        //         document.removeEventListener("click", el.clickOutsideEvent);
        //     },
        // },
        "click-outside": {
            beforeMount(el, binding) {
                el.__clickOutsideHandler__ = (e) => {
                    const clickedEl = e.target;
                    const thisLayer = parseInt(el.getAttribute("data-dropdown-layer"));
                    const clickedLayerEl = clickedEl.closest("[data-dropdown-layer]");
                    const clickedLayer = clickedLayerEl
                        ? parseInt(clickedLayerEl.getAttribute("data-dropdown-layer"))
                        : null;

                    // Don't trigger if click is inside this or a higher layer dropdown
                    if (el.contains(clickedEl) || (clickedLayer && clickedLayer > thisLayer)) {
                        return;
                    }

                    binding.value(e); // Call provided method
                };

                document.addEventListener("click", el.__clickOutsideHandler__);
            },

            unmounted(el) {
                document.removeEventListener("click", el.__clickOutsideHandler__);
                delete el.__clickOutsideHandler__;
            },
        },
    },

    props: {
        options: { type: Array, default: () => [] },
        listType: { type: String, default: "" },
        btnTextTransform: { 
            type: [String, undefined], 
            default: undefined,
            required: false,
            validator: (value) => {
                return ["capitalize", "upper", undefined, ""].includes(value);
            }, 
        },
        indexLabel: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
        customText: { type: Boolean, default: false },
        isCustom: { type: Boolean, default: false },
        menuClass: { type: String, default: "" },
        searchable: { type: Boolean, default: true },
        loading: { type: Boolean, default: false },
        outerLoading: { type: Boolean, default: false },
        modelValue: { type: [String, Object, Array], default: undefined },
        multiple: { type: Boolean, default: false },
        showListTitle: { type: Boolean, default: false },
        closeOnSelect: { type: Boolean, default: true },
        clearOnSelect: { type: Boolean, default: true },
        prefix: { type: Boolean, default: false },
        suffix: { type: Boolean, default: false },
        btnClasses: { type: String, default: "" },
        placeholder: { type: String, default: "" },
        listItemClass: { type: String, default: "" },
        listClass: { type: String, default: "" },
        dataLayerId: { type: Number, default: 1 },
        shadow: { type: Boolean, default: true },
        fetchListOnShow: { type: Boolean, default: false },
        size: { 
            type: String, 
            default: "md",
            validator: (value) => {
                return ["sm", "md", "lg"].includes(value);
            },
        },
    },

    emits: ["update:modelValue", "select", "search", "remove", "fetch-list-show"],

    data() {
        return {
            searchQuery: "",
            reactiveSelected: this.modelValue || undefined, // string, object or array of string or object items
            showDropdown: false,
            searchRef: null,
            inputFocus: false,
            dropdownPosition: "", // New
            dropdownStyles: {}, // New
        };
    },

    computed: {
        textTransformType() {
            if (this.btnTextTransform === "capitalize") {
                return "text--capitalize";
            } else if (this.btnTextTransform === "upper") {
                return "text--upper";
            }
            return "";
        },

        showSearchInput() {
            if (!this.searchable) {
                return false;
            }

            if (this.options.length < 5 && !this.searchQuery) {
                return false;
            }

            return true;
        },

        dynamicZIndex() {
            return 1000 + (this.dataLayerId * 10);
        },
    },

    watch: {
        modelValue(newValue) {
            this.updateDisplayValue(newValue);
        },

        searchQuery(newVal) {
            this.searchList(newVal);
        },

        showDropdown(newVal) {            
            if (newVal) {
                if (this.showSearchInput) {                    
                    this.$nextTick(() => {                        
                        if (this.$refs.searchRef) {
                            this.$refs.searchRef.focus();
                            this.inputFocus = true;
                        }
                    });
                }

                if (this.fetchListOnShow) {
                    this.$emit("fetch-list-show");
                }

                // Calculate position for nested dropdowns
                this.$nextTick(() => {
                    this.calculateDropdownPosition();
                });
            }
        },
    },

    // mounted hook to handle window resize
    mounted() {
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("scroll", this.handleScroll);
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("scroll", this.handleScroll);
    },

    methods: {
        handleResize() {
            if (this.showDropdown) {
                this.calculateDropdownPosition();
            }
        },
    
        handleScroll() {
            if (this.showDropdown && this.dataLayerId > 1) {
                this.calculateDropdownPosition();
            }
        },
        toggleDropdown() {
            this.showDropdown = !this.showDropdown;

            if (this.showDropdown) {
                this.$nextTick(() => {
                    this.calculateDropdownPosition();
                });
            }
        },

        calculateDropdownPosition() {
            if (!this.$refs.dropdownMenu) return;
            
            const dropdown = this.$el;
            const menu = this.$refs.dropdownMenu;
            const rect = dropdown.getBoundingClientRect();
            const menuRect = menu.getBoundingClientRect();
            
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            
            // Calculate if dropdown should open upward
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = menuRect.height || 300; // fallback height
            
            // Calculate if dropdown should open to the left
            const spaceRight = viewportWidth - rect.left;
            const spaceLeft = rect.right;
            const dropdownWidth = menuRect.width || 200; // fallback width
            
            let positionClass = "";
            let styles = {
                zIndex: this.dynamicZIndex,
            };
            
            // Vertical positioning
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                positionClass += " dropup";
            }
            
            // Horizontal positioning
            if (spaceRight < dropdownWidth && spaceLeft > dropdownWidth) {
                positionClass += " dropdown-right";
            }
            
            // For nested dropdowns, use fixed positioning
            if (this.dataLayerId > 1) {
                styles.position = "fixed";
                styles.top = `${rect.bottom}px`;
                styles.left = `${rect.left}px`;
                styles.minWidth = `${rect.width}px`;
                
                // Adjust if would go off-screen
                if (rect.bottom + dropdownHeight > viewportHeight) {
                    styles.top = `${rect.top - dropdownHeight}px`;
                }
                
                if (rect.left + dropdownWidth > viewportWidth) {
                    styles.left = `${rect.right - dropdownWidth}px`;
                }
            }
        
            this.dropdownPosition = positionClass;
            this.dropdownStyles = styles;
        },

        getUniqueId(item) {
            return item._id || item[this.indexLabel] || item;
        },

        closeDropdown() {
            this.showDropdown = false;
            this.dropdownPosition = "";
            this.dropdownStyles = {};
        },

        computeOptionItem(item) { // returns a string or an object            
            if (typeof item === "string") {
                return item;
            }
            
            if (typeof item === "object") {
                if (!this.indexLabel) {
                    return undefined;
                }
                return item[this.indexLabel];
            } 

            return item;
        },

        updateDisplayValue(value) { // string, object or an array            
            if (!value) return;

            if (this.multiple && Array.isArray(this.reactiveSelected)) { // model value is an array
                this.reactiveSelected = value;
                return;
            }

            // model value is an object or string
            const selectedItem = this.options.find((val) => {     
                // string
                if ((typeof value === "string" && typeof val === "string") && val === value) {
                    return value;  // arts
                }  
                
                // object 
                if ((typeof value === "object" && typeof val === "object") && this.indexLabel) {
                    return val[this.indexLabel] === value[this.indexLabel];
                }
            });

            if (!selectedItem) {
                this.reactiveSelected = undefined;
            } else {
                this.reactiveSelected = selectedItem;
            }
        },

        onSelect(item) {
            this.$emit("select", item);
            
            if (this.closeOnSelect) {
                this.showDropdown = false;
            }
            
            if (this.clearOnSelect) {
                this.searchQuery = "";
            }
        },

        clearSearch() {
            this.searchQuery = "";
        },

        searchList(newVal) {                        
            debounce(() => {
                this.$emit("search", newVal);
            }, 200, "search-list");
        },
    },
};

</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.not-found-icon {
    border: 1px solid transparent;
    background: #f7f7f7;
    border-radius: 50%;
    padding: 6px 3px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        fill: #94a4bd;
    }
}
</style>